import type { CMSPageType } from '@backmarket/http-api/src/api-specs-content/models/cms-page-type'
import {
  BCP_47_LOCALES,
  type Bcp47Locale,
} from '@backmarket/http-api/src/standards/Locale'

import { HOME } from '~/scopes/home/route-names'
import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'
import { ROUTES as PLP_ROUTES } from '~/scopes/product-list/route-names'

import { CMS } from '../routes-names'

/**
 * Mapping between the route name and its corresponding page type in the CMS.
 * The page type is provided to the content service call.
 */
export const CMS_PAGE_TYPE_BY_ROUTE_NAME = {
  [CMS.ABOUT_US]: 'core',
  [CMS.ARTICLE]: 'article',
  [CMS.ARTICLE_ROOT_HUB]: 'core',
  [CMS.BACK_STORIES]: 'back-stories',
  [CMS.BACK_STORIES_CATEGORY]: 'back-stories',
  [CMS.BRAND_STORE]: 'brand-store',
  [CMS.BRAND_STORE_CATEGORY]: 'brand-store',
  [CMS.BUYBACK]: 'buyback',
  [CMS.BUYBACK_CATEGORY]: 'buyback',
  [CMS.CAREERS_HOME]: 'careers-page',
  [CMS.CAREERS_CATEGORY]: 'careers-page',
  [CMS.E2E_PAGE]: 'end2EndTests',
  [CMS.END_FAST_TECH]: 'core',
  [CMS.EVENT]: 'event-page',
  [CMS.GIFT_FINDER_EVENT]: 'gift-finder',
  [CMS.GIFT_FINDER_MAIN]: 'gift-finder',
  [CMS.GIFT_FINDER_HOME]: 'gift-finder',
  [CMS.LEGAL_PREVIOUS_VERSION]: 'legal-document',
  [CMS.LEGAL_PAGE]: 'legal-page',
  [CMS.REFER_FRIEND]: 'core',
  [CMS.REPAIR_AND_CARE]: 'core',
  [CMS.SELLER]: 'seller',
  [CMS.STATEMENT]: 'core',
  [CMS.SERVICE]: 'service',
  [CMS.MILITARY_DISCOUNT]: 'core',
  [CMS.STUDENT_DISCOUNT]: 'core',
  [HOME]: 'core',
  [PRODUCT_ROUTES.MASTER_PRODUCT_PAGE]: 'core',
  [CMS.LIFESTYLE]: 'lifestyle',
  [PLP_ROUTES.UNIVERSE]: 'universe-page',
  [CMS.PROMISE]: 'core',
  [CMS.GIFTS]: 'core',
  [CMS.EXPRESS_REPLACEMENT]: 'core',
  [CMS.CUSTOMER_CARE]: 'core',
  [CMS.TRANSLATIONS]: 'core',
} satisfies Record<string, CMSPageType>

export const PAGE_NAME_BY_PLP_UUID: Partial<
  Record<Bcp47Locale, Record<string, string>>
> = {
  [BCP_47_LOCALES.de_DE]: {
    '630dab14-5051-49b9-bc7b-bb20876d4850': 'laptops',
    '6c290010-c0c2-47a4-b68a-ac2ec2b64dca': 'smartphones',
    '0a092eda-8a90-4b60-85bd-8a3123b2fd84': 'gaming',
    '5a3cfa21-b588-49b1-b4e9-2636bec68ada': 'tablets',
    '6a6b4d5a-c50d-4a61-9a7a-3c3b1ce00e50': 'audio',
    '4fa474af-6947-436b-8fd5-31a7d388a3c0': 'appliances',
    '08cf25c8-1084-4061-a84e-c76947c6c59e': 'obsolete',
    'e208f741-a400-484e-809f-4bd76653f96e': 'retro-tech',
  },
  [BCP_47_LOCALES.es_ES]: {
    '630dab14-5051-49b9-bc7b-bb20876d4850': 'laptops',
    '6c290010-c0c2-47a4-b68a-ac2ec2b64dca': 'smartphones',
    '0a092eda-8a90-4b60-85bd-8a3123b2fd84': 'gaming',
    '5a3cfa21-b588-49b1-b4e9-2636bec68ada': 'tablets',
    '6a6b4d5a-c50d-4a61-9a7a-3c3b1ce00e50': 'audio',
    '4fa474af-6947-436b-8fd5-31a7d388a3c0': 'appliances',
    '08cf25c8-1084-4061-a84e-c76947c6c59e': 'obsolete',
    'e208f741-a400-484e-809f-4bd76653f96e': 'retro-tech',
  },
  [BCP_47_LOCALES.fr_FR]: {
    '630dab14-5051-49b9-bc7b-bb20876d4850': 'laptops',
    '6c290010-c0c2-47a4-b68a-ac2ec2b64dca': 'smartphones',
    '0a092eda-8a90-4b60-85bd-8a3123b2fd84': 'gaming',
    '5a3cfa21-b588-49b1-b4e9-2636bec68ada': 'tablets',
    '6a6b4d5a-c50d-4a61-9a7a-3c3b1ce00e50': 'audio',
    '4fa474af-6947-436b-8fd5-31a7d388a3c0': 'appliances',
    '08cf25c8-1084-4061-a84e-c76947c6c59e': 'obsolete',
    'e208f741-a400-484e-809f-4bd76653f96e': 'retro-tech',
  },
  [BCP_47_LOCALES.en_GB]: {
    '630dab14-5051-49b9-bc7b-bb20876d4850': 'laptops',
    '6c290010-c0c2-47a4-b68a-ac2ec2b64dca': 'smartphones',
    '0a092eda-8a90-4b60-85bd-8a3123b2fd84': 'gaming',
    '5a3cfa21-b588-49b1-b4e9-2636bec68ada': 'tablets',
    '6a6b4d5a-c50d-4a61-9a7a-3c3b1ce00e50': 'audio',
    '4fa474af-6947-436b-8fd5-31a7d388a3c0': 'appliances',
    '08cf25c8-1084-4061-a84e-c76947c6c59e': 'obsolete',
    'e208f741-a400-484e-809f-4bd76653f96e': 'retro-tech',
  },
  [BCP_47_LOCALES.en_US]: {
    '02661471-d1ce-4d43-88e3-4bc4b7d4c8d6': 'laptops',
    '0744fd27-8605-465d-8691-3b6dffda5969': 'smartphones',
    'afa370e4-ee3a-426a-a783-c94c899d664a': 'gaming',
    '076e3232-bda6-424b-81a4-9a6c9c08e8ee': 'tablets',
    '91ffe3ce-47dd-4e42-9096-ccf96412f3f9': 'audio',
    'eb710cac-9e9d-4db2-97c7-f56194652295': 'appliances',
    '4334e8f0-e17c-4045-bc9a-bf3b0a2ce276': 'obsolete',
    '9883280c-e28b-49ae-9ebe-07753f6ab18a': 'retro-tech',
  },
}

export const OWNER_BY_PAGE_TYPE = {
  article: '[COM]',
  'back-stories': '[COM]',
  'brand-store': '[COM]',
  buyback: '[BBACK]',
  'careers-page': '[COM]',
  core: '[COM]',
  end2EndTests: '[COM]',
  'event-page': '[COM]',
  'gift-finder': '[COM]',
  'legal-document': '[COM]',
  'legal-page': '[COM]',
  seller: '[SELLER]',
  service: '[B2CS]',
  'universe-page': '[SR]',
  lifestyle: '[SR]',
} satisfies Record<CMSPageType, string>

export const PAGE_TYPE_WITH_SPECIFIC_FIELD: CMSPageType[] = [
  CMS_PAGE_TYPE_BY_ROUTE_NAME[CMS.LEGAL_PAGE],
  CMS_PAGE_TYPE_BY_ROUTE_NAME[CMS.LEGAL_PREVIOUS_VERSION],
]

export const PAGE_TYPE_WITH_NO_PARAMS_CORRECTION: CMSPageType[] = [
  CMS_PAGE_TYPE_BY_ROUTE_NAME[CMS.LEGAL_PAGE],
  CMS_PAGE_TYPE_BY_ROUTE_NAME[CMS.LEGAL_PREVIOUS_VERSION],
  CMS_PAGE_TYPE_BY_ROUTE_NAME[CMS.CAREERS_HOME],
  CMS_PAGE_TYPE_BY_ROUTE_NAME[CMS.CAREERS_CATEGORY],
  CMS_PAGE_TYPE_BY_ROUTE_NAME[CMS.GIFT_FINDER_MAIN],
]
